import { useMemo, useState } from "react";
import { useLang } from "../lib/useLang";
import { scheduleDays, slotsForDay, dayLabel } from "../lib/schedule";

/** ASAP vs scheduled (later) time picker. value is an epoch ms, or null = ASAP. */
export function ScheduleField({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (ts: number | null) => void;
}) {
  const { t, lang } = useLang();
  const days = useMemo(() => scheduleDays(), []);
  // First day that actually has selectable slots — today can be fully booked.
  const firstAvailable = useMemo(() => {
    for (const d of days) {
      const s = slotsForDay(d);
      if (s.length) return { day: d, slot: s[0] };
    }
    return null;
  }, [days]);

  const [later, setLater] = useState(value != null);
  const [dayKey, setDayKey] = useState(firstAvailable?.day.key ?? days[0]?.key ?? "");

  const day = days.find((d) => d.key === dayKey) ?? days[0];
  const slots = useMemo(() => (day ? slotsForDay(day) : []), [day]);

  function chooseAsap() {
    setLater(false);
    onChange(null);
  }

  function chooseLater() {
    setLater(true);
    // Always land on a real future slot. If the current day has none left,
    // jump to the first day that does — never leave "Schedule" with no time
    // selected (which would silently fall back to ASAP at submit).
    if (value == null) {
      if (slots[0]) {
        onChange(slots[0].ts);
      } else if (firstAvailable) {
        setDayKey(firstAvailable.day.key);
        onChange(firstAvailable.slot.ts);
      }
    }
  }

  function pickDay(key: string) {
    setDayKey(key);
    const d = days.find((x) => x.key === key);
    const s = d ? slotsForDay(d) : [];
    onChange(s[0]?.ts ?? null);
  }

  return (
    <section className="card space-y-3 p-4">
      <h2 className="font-bold text-ink">{t("schedule.title")}</h2>

      <div className="flex gap-1 rounded-xl bg-black/5 p-1">
        <Tab active={!later} onClick={chooseAsap}>
          ⚡ {t("schedule.asap")}
        </Tab>
        <Tab active={later} onClick={chooseLater}>
          🗓️ {t("schedule.later")}
        </Tab>
      </div>

      {later && (
        <div className="space-y-3">
          {/* Day picker */}
          <div className="flex flex-wrap gap-2">
            {days.map((d) => {
              const on = d.key === dayKey;
              const hasSlots = slotsForDay(d).length > 0;
              return (
                <button
                  key={d.key}
                  disabled={!hasSlots}
                  onClick={() => pickDay(d.key)}
                  className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                    on
                      ? "border-brand bg-brand text-white"
                      : hasSlots
                        ? "border-black/10 bg-white text-gray-700 hover:border-brand/40"
                        : "cursor-not-allowed border-black/5 bg-black/[.03] text-black/30"
                  }`}
                >
                  {dayLabel(d, lang)}
                </button>
              );
            })}
          </div>

          {/* Time slots */}
          {slots.length === 0 ? (
            <p className="text-sm font-semibold text-amber-700">{t("schedule.noSlots")}</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {slots.map((s) => {
                const on = value === s.ts;
                return (
                  <button
                    key={s.ts}
                    onClick={() => onChange(s.ts)}
                    className={`rounded-lg border px-3 py-1.5 text-sm font-semibold transition ${
                      on
                        ? "border-brand bg-brand/10 text-brand"
                        : "border-black/10 bg-white text-gray-700 hover:border-brand/40"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 rounded-lg py-1.5 text-sm font-semibold transition ${
        active ? "bg-white text-brand shadow-sm" : "text-black/55"
      }`}
    >
      {children}
    </button>
  );
}
